import React, { useState, useCallback } from 'react';
import { UploadIcon } from './icons';
import { useAuth } from '../contexts/AuthContext';

interface ImageUploaderProps {
  onImageUpload: (base64Image: string, mimeType: string) => void;
  freeTrialUsed?: number;
  hideTitle?: boolean;
  scrollToPlanInfo?: () => void;
  userPlan?: any;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({ onImageUpload, freeTrialUsed = 0, hideTitle = false, scrollToPlanInfo, userPlan: propUserPlan }) => {
  const [error, setError] = useState<string | null>(null);
  const { user, userPlan, isPaidUser, isLoading, planLoading } = useAuth();

  // デバッグ用：ユーザー情報とプラン情報をコンソールに出力
  React.useEffect(() => {
    console.log('🔍 ImageUploader - Debug Info:', {
      user: user ? `UID: ${user.uid}, Email: ${user.email}` : 'null',
      userPlan: userPlan,
      propUserPlan: propUserPlan,
      isPaidUser,
      isLoading,
      planLoading,
      timestamp: new Date().toLocaleTimeString()
    });
    
    // クレジット計算の詳細ログ
    const remainingCredits = userPlan?.creditsRest || 0;
    
    console.log('ImageUploader - Credit calculation:', {
      isPaidUser,
      remainingCredits,
      userPlanPlan: userPlan?.plan,
      userPlanCreditsRest: userPlan?.creditsRest
    });
  }, [user, userPlan, isPaidUser, freeTrialUsed, isLoading, planLoading]);

  // プラン別のクレジット上限を取得
  const getCreditLimit = () => {
    if (!isPaidUser) {
      return 3; // 無料体験は3回
    }
    switch (userPlan?.plan) {
      case 'standard': return 10;   // ¥500/買い切り → 10枚
      case 'pro': return 25;        // ¥1,000/買い切り → 25枚
      case 'pro_max': return 200;   // ¥5,000/月 → 200枚
      default: return 3;
    }
  };

  // Firebaseから直接creditsRestを取得（Firebaseが単一の情報源）
  const remainingCredits = userPlan?.creditsRest || 0;
  
  // デバッグ用：クレジット計算結果をコンソールに出力
  console.log('ImageUploader Credit Debug:', {
    remainingCredits,
    isPaidUser,
    freeTrialUsed,
    creditsRest: userPlan?.creditsRest,
    userPlan: userPlan?.plan
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('無効なファイル形式です。JPEG、PNG、またはWEBP形式の画像をアップロードしてください。');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setError('ファイルサイズが大きすぎます。10MB未満の画像をアップロードしてください。');
      return;
    }
    
    setError(null);
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const base64String = loadEvent.target?.result as string;
      onImageUpload(base64String, file.type);
    };
    reader.onerror = () => {
        setError('ファイルの読み込みに失敗しました。');
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = useCallback((event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback((event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      // Create a synthetic event to reuse the change handler logic
      const syntheticEvent = {
        target: { files: event.dataTransfer.files },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileChange(syntheticEvent);
    }
  }, [onImageUpload]);


  return (
    <div className="max-w-2xl mx-auto text-center">
      <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 shadow-sm">
        {!hideTitle && (
          <>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">写真をアップロード</h2>
            <p className="text-slate-500 mb-4 sm:mb-6">
              まず、あなたの顔がはっきりと写った正面の写真をアップロードしてください。
            </p>
          </>
        )}

        {/* クレジット表示 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">
                {planLoading || isLoading ? (
                  'プラン情報を読み込み中...'
                ) : user && isPaidUser ? (
                  userPlan?.plan === 'standard' ? 'Standard プラン' : 
                  userPlan?.plan === 'pro' ? 'Pro プラン' : 
                  userPlan?.plan === 'pro_max' ? 'Pro Max プラン' : 
                  'プラン情報を読み込み中...'
                ) : (
                  '無料プラン'
                )}
              </h3>
              <p className="text-sm text-blue-700">
                {planLoading || isLoading ? (
                  '読み込み中...'
                ) : (
                  <>
                    残り <span className="font-bold">{remainingCredits}</span> 回
                  </>
                )}
              </p>
              {process.env.NODE_ENV === 'development' && (
                <p className="text-xs text-gray-500 mt-1">
                  Debug: freeTrialUsed={freeTrialUsed}, remainingCredits={remainingCredits}, userPlan={JSON.stringify(userPlan)}, isPaidUser={isPaidUser}, isLoading={isLoading}, planLoading={planLoading}
                </p>
              )}
            </div>
              <div className="text-right">
                <div className="w-16 h-16 relative">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center border-2 border-blue-300">
                    <span className="text-sm font-bold text-blue-900">
                      {remainingCredits}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {remainingCredits <= 0 && (
              <div className="mt-2">
                <p className="text-sm text-red-600 font-medium">
                  {isPaidUser ? 'クレジットが不足しています。プランをアップグレードしてください。' : '無料体験の制限に達しました。有料プランにアップグレードしてください。'}
                </p>
                {scrollToPlanInfo && (
                  <button
                    onClick={scrollToPlanInfo}
                    className="text-sm text-blue-600 hover:text-blue-800 underline mt-1 block"
                  >
                    → プラン情報を見る
                  </button>
                )}
              </div>
            )}
          </div>
        <label
          htmlFor="file-upload"
          className="relative block w-full border-2 border-dashed border-slate-300 rounded-lg p-8 sm:p-12 text-center cursor-pointer hover:border-blue-500 hover:bg-slate-100/50 transition-colors"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center justify-center space-y-2 text-slate-500">
            <UploadIcon className="w-10 h-10" />
            <span className="font-medium">
              クリックしてアップロード、またはドラッグ＆ドロップ
            </span>
            <span className="text-sm">PNG, JPG, WEBP (最大10MB)</span>
          </div>
          <input
            id="file-upload"
            name="file-upload"
            type="file"
            className="sr-only"
            accept="image/png, image/jpeg, image/webp"
            onChange={handleFileChange}
          />
        </label>
        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
        <div className="mt-8 text-xs text-slate-600 p-4 bg-slate-100/70 rounded-lg border border-slate-200">
          <h3 className="font-bold mb-2">プライバシーとセキュリティに関する注意:</h3>
          <p>あなたの画像は安全に処理されます。生成後にあなたの写真が保存されることはありません。アップロードすることにより、あなたはその写真に対する権利を所有し、この目的での使用に同意したことになります。</p>
        </div>
      </div>
    </div>
  );
};